import React from 'react';
import { format } from 'date-fns';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Button, IconButton } from '../ui/Button';
import { DAY_LABELS, fromDateKey } from '../../utils/dateUtils';
import styles from './NutritionWeekStrip.module.css';

interface NutritionWeekStripProps {
  weekDateKeys: string[];
  selectedDateKey: string;
  onSelectDate: (dateKey: string) => void;
  onShiftWeek: (deltaWeeks: number) => void;
  getDayMeta: (dateKey: string) => string;
}

export const NutritionWeekStrip: React.FC<NutritionWeekStripProps> = ({
  weekDateKeys,
  selectedDateKey,
  onSelectDate,
  onShiftWeek,
  getDayMeta,
}) => {
  const touchStartX = React.useRef<number | null>(null);
  const touchEndX = React.useRef<number | null>(null);
  const minSwipeDistance = 50;

  const onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchEndX.current = null;
    touchStartX.current = event.targetTouches[0]?.clientX ?? null;
  };

  const onTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    touchEndX.current = event.targetTouches[0]?.clientX ?? null;
  };

  const onTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) {
      return;
    }

    const distance = touchStartX.current - touchEndX.current;

    if (distance > minSwipeDistance) {
      onShiftWeek(1);
    } else if (distance < -minSwipeDistance) {
      onShiftWeek(-1);
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <div className={styles.navRow} aria-label="Week navigation">
      <IconButton
        icon={FaChevronLeft({ 'aria-hidden': 'true' })}
        aria-label="Previous week"
        variant="ghost"
        size="md"
        className={styles.weekArrow}
        onClick={() => onShiftWeek(-1)}
      />

      <div
        className={styles.container}
        role="group"
        aria-label="Nutrition week navigation"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {weekDateKeys.map((dateKey, index) => {
          const isSelected = dateKey === selectedDateKey;
          const dayDate = fromDateKey(dateKey);

          return (
            <Button
              key={dateKey}
              type="button"
              variant="secondary"
              size="md"
              className={`${styles.dayButton} ${isSelected ? styles.selected : ''}`}
              onClick={() => onSelectDate(dateKey)}
              aria-pressed={isSelected}
              aria-label={format(dayDate, 'EEEE MMM d')}
            >
              <span className={styles.dayLabel}>{DAY_LABELS[index]}</span>
              <span className={styles.dayNumber}>{format(dayDate, 'd')}</span>
              <span className={styles.dayMeta}>{getDayMeta(dateKey)}</span>
            </Button>
          );
        })}
      </div>

      <IconButton
        icon={FaChevronRight({ 'aria-hidden': 'true' })}
        aria-label="Next week"
        variant="ghost"
        size="md"
        className={styles.weekArrow}
        onClick={() => onShiftWeek(1)}
      />
    </div>
  );
};
